import { BlueskySettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { BlueskyRecommendationKey, StageType } from '$extensionSrc/utils/enums'

import { StageDef } from '../automationMachineInterfaces'
import { getOptionalElement, shouldUpdateSetting } from '../utils'
import * as blueskyUrls from './blueskyUrls'
import { AUTOMATION_STAGE_STATUSES } from '../genericAutomationStageStatuses'
import { wait } from '../interruptiblePromises'
import {
  getCaseInsensitiveString,
  getNearestElementMatch,
} from '../interruptibleXpath'
import { NeedsUserInputError } from '../needsInputError'
import {
  getIsFulfilledFnFromKey,
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
} from '../recommendationCardDisplayUtils'

// Settings property functions
export const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'On' : 'Off'))

export const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => value)

export const getCantUndoFn = () => defaultCantUndoFn

export function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
export function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

enum StageKey {
  GATHER_SETTING_EMAIL_2FA = 'GATHER_SETTING_EMAIL_2FA',
  UPDATE_SETTING_EMAIL_2FA = 'UPDATE_SETTING_EMAIL_2FA',
  UNDO_SETTING_EMAIL_2FA = 'UNDO_SETTING_EMAIL_2FA',
}

async function get2faLabel() {
  const twofaLabel = await Promise.race([
    getOptionalElement<HTMLElement>(
      document,
      `//*[${getCaseInsensitiveString('text()')} ='two-factor authentication (2fa)']`,
    ),
    getOptionalElement<HTMLElement>(
      document,
      `//*[${getCaseInsensitiveString('text()')} = 'email 2fa enabled']`,
    ),
  ])

  return twofaLabel
}

async function clickEnable2fa(isDry: boolean): Promise<boolean> {
  const twofaLabel = await get2faLabel()
  await wait(500)

  // 2FA disabled = enable button, 2FA enabled = change button
  const enableButton = getNearestElementMatch(
    twofaLabel,
    'button[@aria-label="Enable"]',
    'following',
  )

  if (enableButton && !isDry) {
    enableButton.click()
    return true
  }
  return false
}

async function get2FAConfirmModalEnableButton(): Promise<HTMLElement | null> {
  const [modal, verifyEmailLabel] = await Promise.all([
    getOptionalElement<HTMLElement>(
      document,
      `//div[contains(${getCaseInsensitiveString('text()')}, "enable email 2fa")]/ancestor::div[@role="dialog"]`,
      { timeout: 2000 },
    ),
    getOptionalElement<HTMLElement>(
      document,
      `//*[${getCaseInsensitiveString('text()')} = 'verify your email']`,
      { timeout: 2000 },
    ),
  ]) // Use shorter timeout (default 5s) since we only expect 1 anyways

  if (!modal && verifyEmailLabel) {
    throw new NeedsUserInputError('Please verify your email to enable 2FA')
  }

  const confirmEnable = getOptionalElement<HTMLElement>(
    modal,
    './/button[@aria-label="Enable"]',
  )

  return confirmEnable
}

export const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTING_EMAIL_2FA,
    status: AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: blueskyUrls.privacySecurityUrl,
    action: async (_machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      const twoFASettingLabel = await get2faLabel()
      const is2FAEnabled =
        twoFASettingLabel?.textContent === 'Email 2FA enabled'

      await updateStoredInitialValues(
        {
          [BlueskySettingKey.EMAIL_2FA]: is2FAEnabled,
        },
        BlueskyRecommendationKey.BLUESKY_2FA,
      )
    },
  },
]

export const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTING_EMAIL_2FA,
    status: AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: blueskyUrls.privacySecurityUrl,
    action: async (_machineContext, userContext) => {
      const { initialValues, isDry, settingsSelected } = userContext

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          BlueskySettingKey.EMAIL_2FA,
          getIsFulfilledFn,
        )
      ) {
        const clickedEnable2FA = await clickEnable2fa(isDry)

        if (clickedEnable2FA) {
          const confirmEnableButton = await get2FAConfirmModalEnableButton()
          confirmEnableButton.click()
        } else {
          throw new Error('Failed to click enable 2FA button')
        }
      }
    },
  },
]
export const undoStageDefs: StageDef[] = []
